import { gql } from "@apollo/client/core";
import { computed, provide, ref, unref, type MaybeRef, type Ref } from "vue";
import { apolloClient } from "@/graphql/apollo";
import {
  BoardFilterOptionsInjectionKey,
  type BoardFilterFlagOption,
  type BoardFilterIdOption,
  type BoardFilterOptions,
  type BoardFilterUserOption,
} from "./FilterTypes";

const BOARD_FILTER_OPTIONS_QUERY = gql`
  query SQDCBoardFilterOptions($boardId: Int!, $includeArchived: Boolean) {
    SQDCBoardFilterOptions(boardId: $boardId, includeArchived: $includeArchived) {
      eventIds {
        id
        typeId
      }
      actionIds {
        id
        typeId
      }
      eventUsers {
        id
        preferredName
        count
      }
      actionUsers {
        id
        preferredName
        count
      }
      flags {
        id
        name
        abbreviationId
        displayNameId
        color
        type
        systemFlag
        count
      }
    }
  }
`;

type FilterOptionsResult = {
  SQDCBoardFilterOptions: {
    eventIds: {
      id: number;
      typeId: number;
    }[];
    actionIds: {
      id: number;
      typeId: number;
    }[];
    eventUsers: {
      id: number;
      preferredName: string;
      count: number;
    }[];
    actionUsers: {
      id: number;
      preferredName: string;
      count: number;
    }[];
    flags: {
      id: number;
      name: string;
      abbreviationId: number;
      displayNameId: number;
      color?: string | null;
      type?: string;
      systemFlag: boolean;
      count: number;
    }[];
  };
};

const emptyOptions: BoardFilterOptions = {
  eventUsers: [],
  actionUsers: [],
  eventIds: [],
  actionIds: [],
  flags: [],
};

function toUserOption(id: number, preferredName?: string): BoardFilterUserOption {
  const displayName = preferredName ?? `${id}`;
  return {
    id,
    name: displayName.normalize("NFD").replace(/[\u0300-\u036f]/g, ""),
    displayName,
  };
}

function sortUsers(lhs: BoardFilterUserOption, rhs: BoardFilterUserOption) {
  return lhs.name.localeCompare(rhs.name);
}

function sortIds(lhs: BoardFilterIdOption, rhs: BoardFilterIdOption) {
  return rhs.value - lhs.value;
}

function toIdOption({ id, typeId }: { id: number; typeId: number }): BoardFilterIdOption {
  return {
    value: id,
    label: id,
    typeId,
  };
}

function toOptions(data: FilterOptionsResult["SQDCBoardFilterOptions"]): BoardFilterOptions {
  return {
    eventUsers: data.eventUsers
      .map((user) => toUserOption(user.id, user.preferredName))
      .sort(sortUsers),
    actionUsers: data.actionUsers
      .map((user) => toUserOption(user.id, user.preferredName))
      .sort(sortUsers),
    eventIds: data.eventIds.map(toIdOption).sort(sortIds),
    actionIds: data.actionIds.map(toIdOption).sort(sortIds),
    flags: data.flags
      .map((flag): BoardFilterFlagOption => {
        return {
          id: flag.id,
          name: flag.name,
          count: flag.count,
          color: flag.color ?? undefined,
          type: flag.type,
          systemFlag: flag.systemFlag,
          abbreviationId: flag.abbreviationId,
          displayNameId: flag.displayNameId,
        };
      })
      .sort((lhs, rhs) => lhs.name.localeCompare(rhs.name)),
  };
}

export function provideBoardFilterOptions(
  boardId: MaybeRef<number | undefined>,
  includeArchived: MaybeRef<boolean>,
  fallbackOptions?: MaybeRef<BoardFilterOptions | undefined>,
) {
  const loadedOptions = ref<BoardFilterOptions>(emptyOptions);
  const options = computed(() =>
    unref(boardId) == null ? (unref(fallbackOptions) ?? emptyOptions) : loadedOptions.value,
  );
  const loading = ref(false);
  const loadedKey = ref<string>();
  const queryKey = computed(() => `${unref(boardId) ?? "none"}:${unref(includeArchived)}`);
  let inFlight: Promise<void> | undefined;

  async function load() {
    const currentBoardId = unref(boardId);
    if (currentBoardId == null) {
      return;
    }

    const key = queryKey.value;
    if (loadedKey.value === key) {
      return;
    }
    if (inFlight != null) {
      await inFlight;
      if (loadedKey.value !== queryKey.value) {
        await load();
      }
      return;
    }

    loading.value = true;
    inFlight = apolloClient
      .query<FilterOptionsResult>({
        query: BOARD_FILTER_OPTIONS_QUERY,
        variables: {
          boardId: currentBoardId,
          includeArchived: unref(includeArchived),
        },
        fetchPolicy: "cache-first",
      })
      .then(({ data }) => {
        loadedOptions.value = toOptions(data.SQDCBoardFilterOptions);
        loadedKey.value = key;
      })
      .finally(() => {
        loading.value = false;
        inFlight = undefined;
      });

    await inFlight;
  }

  const provided = {
    options: options as Ref<BoardFilterOptions>,
    loading,
    queryKey,
    load,
  };
  provide(BoardFilterOptionsInjectionKey, provided);
  return provided;
}
